/**
 * @file pwa/pending-uploads.ts
 * @description 离线上传暂存层。
 *
 * 设计：
 *  - 用户离线选了文件 → stashOfflineBlob 把 Blob 写进 cache_media（id='pending:<负数>'）
 *  - 分配负数 tempId（递减计数器存在 meta store）放进 attachments 数组
 *  - 后台 worker（pwa/pending-upload-worker.ts）联网时 flush：实际上传 → 拿到正数 realId
 *    → markUploaded → replaceTempIdInOutbox 把 outbox 内待 PATCH 的负数全部换成正数
 *
 * 关键不变量：
 *  - 服务器永远收不到负数 fileId（saveFormInstanceOffline + sync.ts 的 forceOutbox 守卫）
 *  - 同一个会话/浏览器内，tempId 单调递减（避免重启后冲突）
 */

import { getOfflineDb, type OutboxEntry } from '@/pwa/db';

/** stashOfflineBlob 选项 */
export interface StashOptions {
  /** 业务分类（沿用 stage 1 命名） */
  scope: 'form-attachment';
  /** 关联实体类型 */
  ownerType: 'form_instance';
  /** 关联实体 id（form_instance.id） */
  ownerId: number;
}

/** 上传成功后返回给上层的元信息（与 packages/forms 的 UploadedFileMeta 对齐） */
export interface PendingUploadMeta {
  id: number;
  filename: string;
  sizeBytes: number;
  mimeType: string;
}

const META_KEY_NEXT_TEMP_ID = 'next_temp_id';

/**
 * 取下一个负数 tempId 并持久化计数器。
 *
 * 实现：在 meta store 里存 `next_temp_id`，每次调用先读 → 减 1 → 写回，返回新值。
 * 首次调用（key 不存在）时返回 -1。
 */
export async function nextTempId(): Promise<number> {
  const db = await getOfflineDb();
  const tx = db.transaction('meta', 'readwrite');
  const cur = await tx.store.get(META_KEY_NEXT_TEMP_ID);
  const next = (cur?.value ?? 0) - 1;
  await tx.store.put({ value: next }, META_KEY_NEXT_TEMP_ID);
  await tx.done;
  return next;
}

/**
 * 把用户选择的 File（离线场景）暂存到 IndexedDB，分配负数 tempId 返回。
 *
 * 调用方（form fill page 的 onUploadFile）拿到 tempId 后，把它当成 fileId
 * 推进 attachments 数组里——服务器永远不会看到这个负数（saveFormInstanceOffline
 * 的 forceOutbox 守卫保证）。
 */
export async function stashOfflineBlob(file: File, opts: StashOptions): Promise<number> {
  const tempId = await nextTempId();
  const db = await getOfflineDb();
  await db.put('cache_media', {
    id: `pending:${tempId}`,
    blob: file,
    filename: file.name,
    mimeType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
    scope: opts.scope,
    ownerType: opts.ownerType,
    ownerId: opts.ownerId,
    status: 'pending',
    retryCount: 0,
    createdAt: Date.now(),
  });
  return tempId;
}

/** 列出所有 status='pending' 的待上传行（按插入顺序） */
export async function listPending() {
  const db = await getOfflineDb();
  return db.getAllFromIndex('cache_media', 'by-status', 'pending');
}

/** 标记为 uploading（worker 开始处理时调用，防其他 tab/进程重复处理） */
export async function markUploading(tempId: number): Promise<void> {
  const db = await getOfflineDb();
  const key = `pending:${tempId}`;
  const row = await db.get('cache_media', key);
  if (!row) return;
  row.status = 'uploading';
  row.lastAttemptAt = Date.now();
  await db.put('cache_media', row);
}

/** 标记为 uploaded + 写入 realId */
export async function markUploaded(tempId: number, realId: number): Promise<void> {
  const db = await getOfflineDb();
  const key = `pending:${tempId}`;
  const row = await db.get('cache_media', key);
  if (!row) return;
  row.status = 'uploaded';
  row.uploadedFileId = realId;
  await db.put('cache_media', row);
}

/** 上传失败 → status='error'，retryCount++，errorMessage 更新 */
export async function markError(tempId: number, errorMessage: string): Promise<void> {
  const db = await getOfflineDb();
  const key = `pending:${tempId}`;
  const row = await db.get('cache_media', key);
  if (!row) return;
  row.status = 'error';
  row.retryCount = (row.retryCount ?? 0) + 1;
  row.errorMessage = errorMessage;
  row.lastAttemptAt = Date.now();
  await db.put('cache_media', row);
}

/** 获取本地 meta（filename/size/mime），用于 useFileMetas 解析负数 ID */
export async function getMeta(tempId: number): Promise<PendingUploadMeta | undefined> {
  const db = await getOfflineDb();
  const row = await db.get('cache_media', `pending:${tempId}`);
  if (!row) return undefined;
  return {
    id: tempId,
    filename: row.filename,
    sizeBytes: row.sizeBytes,
    mimeType: row.mimeType,
  };
}

/** 获取 Blob 本体，worker 上传时调用 */
export async function getBlob(tempId: number): Promise<Blob | undefined> {
  const db = await getOfflineDb();
  const row = await db.get('cache_media', `pending:${tempId}`);
  return row?.blob;
}

/** 物理删除一行（用于 stage 2 的 markUploaded 后清理；保留映射延后做） */
export async function deleteRow(tempId: number): Promise<void> {
  const db = await getOfflineDb();
  await db.delete('cache_media', `pending:${tempId}`);
}

/**
 * 把 outbox 中所有 form_instance entityType 行的 PATCH body 内
 * `data` 字段所引用的 tempId 替换成 realId。
 *
 * 行为：
 *  - 仅扫 entityType='form_instance' 的行（其他实体类型不动，避免误伤）
 *  - 在 data 的每个字段值里递归找：
 *    1. number[] 类型的字段（file/photo 字段）
 *    2. object 字段下层每个子值的 .attachments 数组（checklist 字段）
 *  - 整数相等比较，命中则替换
 *
 * 注意：不修改 outbox 行的 status/attempts，仅修改 body。
 */
export async function replaceTempIdInOutbox(tempId: number, realId: number): Promise<void> {
  const db = await getOfflineDb();
  const rows = await db.getAllFromIndex('outbox', 'by-entity', 'form_instance');
  for (const row of rows) {
    if (replaceTempIdInBody(row, tempId, realId)) {
      await db.put('outbox', row);
    }
  }
}

/**
 * 内部：扫一个 outbox 条目的 body.data，原地替换 attachments 中的 tempId。
 * 返回 true 表示有任何改动；调用方据此决定是否 put 回库。
 */
function replaceTempIdInBody(row: OutboxEntry, tempId: number, realId: number): boolean {
  const body = row.body as { data?: Record<string, unknown> } | undefined;
  const data = body?.data;
  if (!data || typeof data !== 'object') return false;

  let changed = false;

  for (const [key, val] of Object.entries(data)) {
    // file/photo 字段：number[]
    if (Array.isArray(val) && val.every((x): x is number => typeof x === 'number')) {
      const replaced = val.map((x) => (x === tempId ? realId : x));
      if (replaced.some((x, i) => x !== val[i])) {
        data[key] = replaced;
        changed = true;
      }
      continue;
    }

    // checklist 字段：{ [clauseId]: { result, note, attachments: number[] } }
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      for (const cv of Object.values(val as Record<string, unknown>)) {
        if (cv && typeof cv === 'object' && 'attachments' in cv) {
          const att = (cv as { attachments?: unknown }).attachments;
          if (Array.isArray(att) && att.every((x): x is number => typeof x === 'number')) {
            const replaced = att.map((x) => (x === tempId ? realId : x));
            if (replaced.some((x, i) => x !== att[i])) {
              (cv as { attachments: number[] }).attachments = replaced;
              changed = true;
            }
          }
        }
      }
    }
  }

  return changed;
}
